import React, { useEffect, useState } from 'react';
import * as R from 'ramda';
import { Datagrid, Error, Loading, useQuery } from 'react-admin';
import { Box, makeStyles } from '@material-ui/core';

import {
  AssignmentsProgressField,
  UnitProgressField,
  AssessmentsProgressField,
  LabsProgressField,
  QuizzesProgressField,
} from 'resources/common/cohorts/list/fields';
import { FullNameField } from 'resources/students/fields';
import {
  AssessmentScoreChip,
  AssignmentGradeDistributionField,
  ChipField,
  OfflineList,
} from 'resources/common';
import { getQuizUnitId } from 'resources/common/quizProgress/quizPathUtils';

const useListStyles = makeStyles({
  units: { width: 100 },
  grades: { width: 250 },
  average_grade: { width: 100 },
  average_score: { width: 100 },
  average_quiz_score: { width: 120 },
});

const scoreMap = {
  very_good: 5,
  good: 4,
  normal: 3,
  bad: 2,
  very_bad: 1,
};

const isRequired = R.propEq('required', true);

const toNumber = (value) => {
  if (R.isNil(value) || value === '') return null;
  const number = Number(value);
  return Number.isFinite(number) ? number : null;
};

const normalizeEmail = R.pipe(R.defaultTo(''), String, R.toLower);

const matchesStudent = (student) => (record) => {
  const studentIds = R.reject(R.isNil, [
    student.id,
    student._id,
    student.studentId,
  ]);
  const recordIds = R.reject(R.isNil, [
    record.studentId,
    record.student_id,
    record.student,
    record.userId,
    record.user_id,
  ]);
  const hasMatchingId = R.any((id) => R.includes(id, studentIds), recordIds);
  const studentEmail = normalizeEmail(student.email);
  const recordEmail = normalizeEmail(record.email);

  return hasMatchingId || (studentEmail && studentEmail === recordEmail);
};

const getStudentRecord = (student, records) =>
  R.find(matchesStudent(student), records || []);

const getAverageQuizScoreRounded = (student, quizzes) =>
  R.pipe(
    R.map((quiz) => getStudentRecord(student, R.propOr([], 'submissions', quiz))),
    R.reject(R.isNil),
    R.pluck('globalScore'),
    R.map(toNumber),
    R.reject(R.isNil),
    (scores) => (R.isEmpty(scores) ? null : Math.round(R.mean(scores)))
  )(quizzes);

const getQuizzesForProgress = (quizzes) =>
  R.when(R.any(isRequired), R.filter(isRequired))(quizzes);

const ByStudentsSubtab = (props) => {
  const { record, course } = props;
  const classes = useListStyles();
  const [students, setStudents] = useState([]);

  const assignments = R.pathOr([], [course, 'assignments'], record);
  const assessments = R.pipe(
    R.pathOr([], [course, 'challenges']),
    R.uniqBy(R.prop('assessment_id'))
  )(record);
  const labs = R.pathOr([], [course, 'labs'], record);
  const hasQuizzes = R.hasPath([course, 'quizzes'], record);

  const {
    data: apiStudents,
    loading: studentsLoading,
    error: studentsError,
  } = useQuery({
    type: 'getManyReference',
    resource: 'students',
    payload: {
      target: 'cohort',
      id: record.id,
      pagination: {
        perPage: 100,
      },
      filter: { course: true },
      sort: { field: 'first_name', order: 'ASC' },
    },
  });

  const {
    data: apiQuizzes,
    loading: quizzesLoading,
    error: quizzesError,
  } = useQuery(
    {
      type: 'getManyReference',
      resource: 'quizzes',
      payload: {
        target: 'cohort',
        id: record.id,
        pagination: {
          perPage: 100,
        },
        filter: { course },
        sort: { field: 'first_name', order: 'ASC' },
      },
    },
    { enabled: hasQuizzes }
  );

  useEffect(() => {
    if (apiStudents) {
      const hiddenUnits = R.pathOr([], [course, 'hidden_units'], record);
      const isUnitHidden = (item) =>
        R.includes(getQuizUnitId(item), hiddenUnits);
      const visibleQuizzes = R.reject(isUnitHidden, apiQuizzes || []);
      const quizzesForProgress = getQuizzesForProgress(visibleQuizzes);
      const tableData = R.pipe(
        R.map((student) => {
          const averageGrade = R.pipe(
            R.pathOr([], [course, 'deliveries']),
            R.when(R.any(isRequired), R.filter(isRequired)),
            R.pluck('grade'),
            R.reject(R.isNil),
            R.map(R.prop(R.__, scoreMap)),
            R.mean,
            (mean) => Math.round((mean + Number.EPSILON) * 100) / 100
          )(student);
          const averageScore = R.pipe(
            R.pathOr([], [course, 'challenges']),
            R.when(R.any(isRequired), R.filter(isRequired)),
            R.uniqBy(R.prop('assessment_id')),
            R.pluck('assessment_score_average'),
            R.reject(R.isNil),
            R.mean,
            (mean) => Math.round((mean + Number.EPSILON) * 100) / 100
          )(student);
          const averageAiScore = R.pipe(
            R.pathOr([], [course, 'labs']),
            R.when(R.any(isRequired), R.filter(isRequired)),
            R.map((lab) => {
              const lastAttempt = R.last(R.propOr([], 'attempts', lab));
              return R.pipe(
                R.propOr([], 'evaluables'),
                R.reject(R.isNil),
                R.pluck('score'),
                (scores) =>
                  R.defaultTo(
                    null,
                    Math.round((R.mean(scores) + Number.EPSILON) * 100) / 100
                  )
              )(lastAttempt);
            }),
            R.reject(R.isNil),
            (averages) =>
              R.defaultTo(
                null,
                Math.round((R.mean(averages) + Number.EPSILON) * 100) / 100
              )
          )(student);
          const averageCutScore = R.pipe(
            R.pathOr([], [course, 'challenges']),
            R.when(R.any(isRequired), R.filter(isRequired)),
            R.uniqBy(R.prop('assessment_id')),
            R.pluck('assessment_cut_score'),
            R.reject(R.isNil),
            R.mean,
            (mean) => Math.round((mean + Number.EPSILON) * 100) / 100
          )(student);
          return R.mergeDeepRight(student, {
            [course]: {
              average_ai_score: averageAiScore,
              average_cut_score: averageCutScore,
              average_grade: averageGrade,
              average_score: averageScore,
              average_quiz_score: getAverageQuizScoreRounded(
                student,
                quizzesForProgress
              ),
            },
          });
        })
      )(apiStudents);
      setStudents(tableData);
    }
  }, [apiQuizzes, apiStudents, course, record]);

  const loading = studentsLoading || (hasQuizzes && quizzesLoading);
  const progressError = studentsError || (hasQuizzes && quizzesError);

  if (loading)
    return (
      <Box p="16px">
        <Loading />
      </Box>
    );
  if (progressError)
    return (
      <Box p="16px">
        <Error error={progressError} errorInfo={progressError} />
      </Box>
    );

  if (R.isEmpty(students)) {
    return null;
  }

  return (
    <OfflineList
      list={students}
      uniqueKey="id"
      defaultSort={{ field: 'first_name', order: 'ASC' }}
      nullsLastFields={[`${course}.average_quiz_score`]}
      sorters={{
        units_progress: R.pipe(R.pathOr([], [course, 'progress']), R.length),
        assignments_progress: R.pipe(
          R.pathOr([], [course, 'deliveries']),
          R.filter(R.pipe(R.propOr('', 'status'), R.startsWith('delivered'))),
          R.length
        ),
        assessments_progress: R.pipe(
          R.pathOr([], [course, 'challenges']),
          R.filter(R.propEq('status', 'delivered')),
          R.length
        ),
        [`${course}.deliveries`]: R.pipe(
          R.pathOr([], [course, 'deliveries']),
          R.filter(R.propEq('grade', 'very_good')),
          R.length
        ),
        [`${course}.average_quiz_score`]: R.path([course, 'average_quiz_score']),
      }}
    >
      <Datagrid
        rowClick={(id) => {
          window.open(`/#/students/${id}/show/${course}_progress`, '_blank');
        }}
      >
        <FullNameField />
        {R.pathOr(0, [course, 'learning_units_count'], record) > 0 && (
          <UnitProgressField
            label="Unit Progress"
            source="units_progress"
            course={course}
            cohort={record}
            cellClassName={classes.units}
          />
        )}
        <AssignmentsProgressField
          label={`${
            R.any(R.propEq('required', true), assignments) ? 'Req. ' : ''
          }Assignments`}
          source="assignments_progress"
          course={course}
          cohort={record}
        />
        {R.hasPath([course, 'labs'], record) && (
          <LabsProgressField
            label={`${
              R.any(R.propEq('required', true), labs) ? 'Req. ' : ''
            }Labs`}
            source="labs"
            course={course}
            cohort={record}
          />
        )}
        {hasQuizzes && (
          <QuizzesProgressField
            label={`${
              R.any(R.propEq('required', true), apiQuizzes || []) ? 'Req. ' : ''
            }Quizzes`}
            source="quizzes"
            course={course}
            cohort={record}
            quizzes={apiQuizzes || []}
          />
        )}
        {hasQuizzes && (
          <ChipField
            label="Avg. Quiz Score"
            source={`${course}.average_quiz_score`}
            chipStyle="labGrade"
            emptyLabel="—"
            cellClassName={classes.average_quiz_score}
          />
        )}
        <AssignmentGradeDistributionField
          label="Grades"
          source={`${course}.deliveries`}
          sortable={false}
          cellClassName={classes.grades}
        />
        <ChipField
          label="Avg. Grade"
          source={`${course}.average_grade`}
          chipStyle="assignmentGrade"
          cellClassName={classes.average_grade}
        />
        <AssessmentsProgressField
          label={`${
            R.any(R.propEq('required', true), assessments) ? 'Req. Passed ' : ''
          }Assessments`}
          course={course}
          cohort={record}
          source="assessments_progress"
        />
        <AssessmentScoreChip
          label="Avg. Score"
          scoreSource={`${course}.average_score`}
          cutScoreSource={`${course}.average_cut_score`}
        />
        {R.hasPath([course, 'labs'], record) && (
          <ChipField
            label="Avg. AI Score"
            source={`${course}.average_ai_score`}
            chipStyle="labGrade"
            cellClassName={classes.average_ai_score}
          />
        )}
      </Datagrid>
    </OfflineList>
  );
};

export default ByStudentsSubtab;
